import {ElementRef} from "@angular/core";
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {
  EFirmRoles,
  IWhiteLabelSettingsSchema,
  WhiteLabelSettingsFormControl,
  WhiteLabelSettingsFormKeys,
  WhiteLabelSettingsFormRawValue,
  WhiteLabelSettingsModel,
  WhiteLabelSettingsResetScope,
  WhiteLabelSettingsTab,
} from '@atlas-workspace/shared/models';
import {NgbModalOptions, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {TranslateService} from "@ngx-translate/core";
import {classToPlain} from "class-transformer";
import {parseInt} from 'lodash';
import {IConfig} from 'ngx-mask';
import {Observable} from "rxjs";

import {
  whiteLabelSettingsEmailSchema,
  whiteLabelSettingsSchema,
  whiteLabelSettingsWebSchema
} from "./white-label.schema";

const defaultWhiteLabelWebSettingsLogoPreviewPath = '/assets/client-logo-v3.svg';
const defaultWhiteLabelEmailSettingsLogoPreviewPath = '/assets/client-logo-email.svg';

export const whiteLabelHexColorInputMaskPatternConfig: IConfig['patterns'] = {
  o: {
    pattern: /[A-Fa-f0-9]/,
  },
};

export function getDefaultWhiteLabelSettingsLogoPreviewPath(tab: WhiteLabelSettingsTab): string {
  if (tab === WhiteLabelSettingsTab.Email) {
    return defaultWhiteLabelEmailSettingsLogoPreviewPath;
  }
  return defaultWhiteLabelWebSettingsLogoPreviewPath;
}

export function initWhiteLabelSettingsForm(): FormGroup<WhiteLabelSettingsFormControl> {
  const controls: WhiteLabelSettingsFormControl = <WhiteLabelSettingsFormControl>{};
  const inputs = [...whiteLabelSettingsWebSchema, ...whiteLabelSettingsEmailSchema].flatMap(schema => schema.inputs);

  inputs.forEach((input) => {
    controls[input.formControlName] = new FormControl(null, input.validators);
    input.isAssets && input.boundFileKeys?.forEach((boundFileFormControlName) => {
      controls[boundFileFormControlName] = new FormControl(null);
    });
  });

  return new FormGroup<WhiteLabelSettingsFormControl>(controls);
}

export function fillEmptyFieldsByDefaultValuesOfPlainSettingsObj(
  plainObj: Partial<WhiteLabelSettingsFormRawValue>,
  defaultFormRawValue = whiteLabelSettingsDefaultInputsValues,
): void {
  Object.keys(plainObj).forEach((key) => {
    const typedKey = key as keyof typeof defaultFormRawValue;
    if (typedKey in defaultFormRawValue && !plainObj[typedKey] && plainObj[typedKey] !== 0) {
      plainObj[typedKey] = defaultFormRawValue[typedKey];
    }
  });
}

export function setDefaultValueForEmptyControl(
  control: AbstractControl,
  key: WhiteLabelSettingsFormKeys,
  defaultFormRawValue = whiteLabelSettingsDefaultInputsValues,
  force = false,
): void {
  if (force || !control.value) {
    const plainObj = { [key]: null } as Partial<WhiteLabelSettingsFormRawValue>;
    fillEmptyFieldsByDefaultValuesOfPlainSettingsObj(plainObj, defaultFormRawValue);
    control.setValue(plainObj[key]);
  }
}

/**
 * @relates Record<{formControlName}, {defaultValue}>
 */
export const whiteLabelSettingsDefaultInputsValues: Partial<WhiteLabelSettingsFormRawValue> = {
  // Web
  ...Object.fromEntries(
    whiteLabelSettingsWebSchema.flatMap(s =>
      s.inputs.filter(i => !i.isAssets).map(i => [i.formControlName, i.defaultValue])
    )
  ),
  // Email
  ...Object.fromEntries(
    whiteLabelSettingsEmailSchema.flatMap(s =>
      s.inputs.filter(i => !i.isAssets).map(i => [i.formControlName, i.defaultValue])
    )
  ),
  // Assets Web
  [WhiteLabelSettingsFormKeys.LogoUrl]: defaultWhiteLabelWebSettingsLogoPreviewPath,
  // Assets Email
  [WhiteLabelSettingsFormKeys.EmailLogoUrl]: defaultWhiteLabelEmailSettingsLogoPreviewPath,
};

export const whiteLabelSettingsDefaultColorPickerPreviewParams = {
  backgroundColor: '#FFFFFF',
  borderColor: '#C4C9D4',
};

/**
 * @relates Record<{tab}, Record<{formControlName}, {previewStyleVariable}>>
 */
export const whiteLabelPreviewStyleVariables = {
  [WhiteLabelSettingsTab.Web]: Object.fromEntries(
    whiteLabelSettingsWebSchema.flatMap(s =>
      s.inputs.filter(i => i.previewStyleVariable).map(i => [i.formControlName, i.previewStyleVariable])
    )
  ),
  [WhiteLabelSettingsTab.Email]: Object.fromEntries(
    whiteLabelSettingsEmailSchema.flatMap(s =>
      s.inputs.filter(i => i.previewStyleVariable).map(i => [i.formControlName, i.previewStyleVariable])
    )
  ),
};

const whiteLabelSettingsColorFormKeys = Object.values(whiteLabelSettingsSchema).flatMap(schema =>
  schema.flatMap(section =>
    section.inputs.filter(i => !i.isBorderRadius && !i.isAssets).map(input => input.formControlName)
  )
);
const whiteLabelSettingsBorderRadiusFormKeys = Object.values(whiteLabelSettingsSchema).flatMap(schema =>
  schema.flatMap(section =>
    section.inputs.filter(i => i.isBorderRadius).map(input => input.formControlName)
  )
);

export function formatWhiteLabelSettingsControlValue(key: WhiteLabelSettingsFormKeys, value: any): string {
  const isColor = whiteLabelSettingsColorFormKeys.includes(key);
  if (isColor) {
    return `#${value}`;
  }
  const isBorderRadius = whiteLabelSettingsBorderRadiusFormKeys.includes(key);
  if (isBorderRadius) {
    return `${value}px`;
  }
  return value;
}

export function isLightHexColor(hex: string): boolean {
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const brightnessDegree = 180;

  return luminance > brightnessDegree;
}

export function hasAccessToWhiteLabelBuilder(firmRole: string): boolean {
  return firmRole === EFirmRoles.PrimaryOwner || firmRole === EFirmRoles.Admin;
}

export function toFormDataToUpdate(formRawValue: WhiteLabelSettingsFormRawValue): FormData {
  const fd = new FormData();
  const setPrimitiveFormDataValue = (key: WhiteLabelSettingsFormKeys): void =>
    fd.append(`white_label[${key}]`, String(formRawValue[key]));

  whiteLabelSettingsColorFormKeys.forEach((colorFormKey) => {
    setPrimitiveFormDataValue(colorFormKey);
  });
  whiteLabelSettingsBorderRadiusFormKeys.forEach((borderRadiusFormKey) => {
    setPrimitiveFormDataValue(borderRadiusFormKey);
  });
  // Logo [Web]
  appendAssetToFormDataToUpdate(
    fd,
    formRawValue,
    'logo_attributes',
    WhiteLabelSettingsFormKeys.LogoFileId,
    WhiteLabelSettingsFormKeys.LogoFile,
    WhiteLabelSettingsFormKeys.LogoUrl
  );
  // Logo [Email]
  appendAssetToFormDataToUpdate(
    fd,
    formRawValue,
    'email_logo_attributes',
    WhiteLabelSettingsFormKeys.EmailLogoFileId,
    WhiteLabelSettingsFormKeys.EmailLogoFile,
    WhiteLabelSettingsFormKeys.EmailLogoUrl
  );

  return fd;
}

function appendAssetToFormDataToUpdate(
  fd: FormData,
  formRawValue: WhiteLabelSettingsFormRawValue,
  prefix: string,
  fileIdKey: WhiteLabelSettingsFormKeys,
  fileKey: WhiteLabelSettingsFormKeys,
  urlKey: WhiteLabelSettingsFormKeys
): void {
  const fileId = formRawValue[fileIdKey];
  const file = formRawValue[fileKey];
  const url = formRawValue[urlKey];
  const attrPath = `white_label[${prefix}]`;

  if (fileId && !file && !url) {
    // No new logo, remove old logo - delete file
    fd.append(`${attrPath}[id]`, String(fileId));
    fd.append(`${attrPath}[_destroy]`, 'true');
  } else if (file && !fileId) {
    // Has new logo, no old logo - upload file
    fd.append(`${attrPath}[filename]`, file);
  } else if (file && fileId) {
    // Has new logo, has old logo - replace file
    fd.append(`${attrPath}[id]`, String(fileId));
    fd.append(`${attrPath}[filename]`, file);
  }
}


export function applyPreviewStyleProperties(
  hostElement: ElementRef<HTMLElement>,
  form: FormGroup<WhiteLabelSettingsFormControl>,
  defaultSettings: Partial<WhiteLabelSettingsFormRawValue>,
  formRawValue: Partial<WhiteLabelSettingsFormRawValue>,
  tab: WhiteLabelSettingsTab,
): void {
  Object.keys(formRawValue).forEach((_key) => {
    const key = _key as WhiteLabelSettingsFormKeys;
    if (key === WhiteLabelSettingsFormKeys.LogoUrl || key === WhiteLabelSettingsFormKeys.EmailLogoUrl) {
      return;
    }
    let value = formRawValue[key] || defaultSettings[key];
    if (!value) {
      return;
    }
    const isValidValue = !!form.get(key)?.valid;
    if (!isValidValue) {
      return;
    }
    value = formatWhiteLabelSettingsControlValue(key, value);
    //@ts-ignore
    const variable = whiteLabelPreviewStyleVariables[tab][key];
    if (!variable) {
      return;
    }
    hostElement.nativeElement.style.setProperty(variable, value);
  });
}

export function patchWhiteLabelSettingsFormValue(
  form: FormGroup<WhiteLabelSettingsFormControl>,
  plainObj: Partial<WhiteLabelSettingsFormRawValue>,
  withNullableFiles = true
): void {
  // * Fix wrong ngx-mask empty reflection for number "0" as value.
  // * ngx-mask treats the number "0" as an invalid value
  // * so, convert the numeric value to a string value.
  for (const _key in plainObj) {
    const key = _key as WhiteLabelSettingsFormKeys;
    if (Number.isInteger(plainObj[key])) {
      plainObj[key] = String(plainObj[key]);
    }
  }

  form.patchValue({
    ...plainObj,
    ...(withNullableFiles && {
      [WhiteLabelSettingsFormKeys.LogoFile]: null,
      [WhiteLabelSettingsFormKeys.EmailLogoFile]: null,
    }),
  }, { emitEvent: false });
}

export function openResetConfirmationModal(
  openModalFn: (content: any, options?: NgbModalOptions) => NgbModalRef,
  // * Avoid directly defining NewestConfirmModalComponent to avoid circular dependencies
  newestConfirmModalComponent: unknown,
  translateService: TranslateService,
  resetScope: WhiteLabelSettingsResetScope
): Observable<boolean> {
  const confirmModalRef = openModalFn(newestConfirmModalComponent, {
    centered: true,
    windowClass: 'confirm-action-modal-v2',
  });
  confirmModalRef.componentInstance.title = `${translateService.instant('Title.Reset_changes')}`;
  const isResetAllScope = (
    resetScope === WhiteLabelSettingsResetScope.Web || resetScope === WhiteLabelSettingsResetScope.Email
  );
  const description = isResetAllScope ? 'Description.Reset_changes' : 'Description.Reset_category_changes';
  confirmModalRef.componentInstance.description = translateService.instant(description);
  confirmModalRef.componentInstance.cancelBtnLabel = translateService.instant('Button.Cancel');
  confirmModalRef.componentInstance.button.text = `${translateService.instant('Button.Reset_changes')}`;
  confirmModalRef.componentInstance.buttonRed = false;
  confirmModalRef.componentInstance.icon = 'basket.svg';
  confirmModalRef.componentInstance.closeOnConfirm = true;

  return confirmModalRef.componentInstance.passConfirm;
}

export function preparePlainObjToFillResetFormSection(
  resetScope: WhiteLabelSettingsResetScope,
  schema: IWhiteLabelSettingsSchema[],
  settings: WhiteLabelSettingsModel
): Partial<WhiteLabelSettingsFormRawValue> {
  let sections: WhiteLabelSettingsResetScope[] = [resetScope];
  if (resetScope === WhiteLabelSettingsResetScope.Web) {
    sections = [
      WhiteLabelSettingsResetScope.WebGeneral,
      WhiteLabelSettingsResetScope.WebPrimaryButton,
      WhiteLabelSettingsResetScope.WebSecondaryButton,
      WhiteLabelSettingsResetScope.WebText,
      WhiteLabelSettingsResetScope.WebWidget,
      WhiteLabelSettingsResetScope.WebAssets,
    ];
  }
  if (resetScope === WhiteLabelSettingsResetScope.Email) {
    sections = [
      WhiteLabelSettingsResetScope.EmailGeneral,
      WhiteLabelSettingsResetScope.EmailButton,
      WhiteLabelSettingsResetScope.EmailAssets,
    ];
  }

  const updatedPlainObj = <WhiteLabelSettingsFormRawValue>classToPlain(settings, { exposeUnsetFields: false });
  const resetPlainObj: Partial<WhiteLabelSettingsFormRawValue> = {};

  sections.forEach((sectionScope) => {
    const section = schema.find((s) => s.resetScope === sectionScope);
    if (!section) return;

    section.inputs.forEach((input) => {
      if (input.hidden) return;
      if (section.isAssets) {
        resetPlainObj[input.formControlName] = null;
        input.boundFileKeys?.forEach((key) => {
          resetPlainObj[key] = updatedPlainObj[key];
        });
      } else {
        resetPlainObj[input.formControlName] = updatedPlainObj[input.formControlName];
      }
    });
  });

  return resetPlainObj;
}
